import { __ } from '@wordpress/i18n'

import styled from '@emotion/styled'
import { useEffect, useState } from '@wordpress/element'
import { applyFilters } from "@wordpress/hooks"
import { FallbackBox } from '@wptravelengine/public/commonComponents'
import { PricingItem, PricingTable, Select } from '@wptravelengine/public/fragments'
import { keyBy } from 'lodash'
import { useQuery } from 'react-query'

const ServiceItem = ({ item, onChange, state }) => {
    const isOptSoldOut = (opt) => Number.isInteger(opt?.avaQty) && opt.avaQty === 0
    const [selectedOption, setSelectedOption] = useState(
        (item.options.find(opt => !isOptSoldOut(opt)) || item.options[0]).key
    )
    const { summary: { extraServices } } = state

    const { title, options, required, multiple } = item

    const itemProps = { required }

    const extraServicesSummary = keyBy(extraServices, 'id')

    const getMax = (opt) => Number.isInteger(opt?.avaQty) ? opt.avaQty : Infinity

    if (multiple && options.length > 1) {
        return options.map((option) => {
            const { price, label, key, title } = option
            const defaultItem = <PricingItem
                key={key}
                {...{ label: title || label, price, direction: 'vertical' }}
                perTextLabel={`/ ${option.serviceUnit.label}`}
                helpText={option.description}
                counter={{
                    min: 0,
                    max: getMax(option),
                    value: extraServicesSummary[key]?.qty ?? 0,
                    onChange: count => onChange(option, count),
                }}
            />
            return applyFilters(
                'wptravelengine.extraServices.optionItem',
                defaultItem,
                { option, item, isAdvancedCard: true }
            )
        })
    }

    const dropdownOptions = options.map(({ label, key, avaQty = '' }) => {
        if (Number.isInteger(avaQty) && avaQty <= 0) return null
        return { value: key, label }
    }).filter(Boolean)

    const afterLabel = !multiple && options.length > 1 ?
        <Select options={dropdownOptions}
            value={selectedOption}
            onChange={value => {
                if (isOptSoldOut(options.find(o => o.key === value))) return
                setSelectedOption(value)
                onChange({ key: value, price: 0 }, 0)
            }} /> : null

    const option = options.find(({ key }) => key === selectedOption)
    const displayLabel = options.length === 1 && options[0] ? (options[0]?.title || options[0]?.label) : (option?.title || title)

    const defaultItem = <PricingItem
        {...itemProps}
        perTextLabel={option ? `/ ${option.serviceUnit.label}` : ''}
        price={option.price}
        afterLabel={afterLabel}
        label={displayLabel}
        helpText={option.description}
        counter={{
            min: 0,
            max: getMax(option),
            value: extraServicesSummary[option?.key]?.qty ?? 0,
            onChange: count => onChange(option, count),
        }} />

    return applyFilters(
        'wptravelengine.extraServices.optionItem',
        defaultItem,
        { option, item, isAdvancedCard: false }
    )
}

const withMultipleOptions = (Component) => ({ service, ...props }) => {
    const { multiple, options, id, title, required } = service
    const showParentTitle = multiple && options.length >= 1

    return showParentTitle ? <div className="wte-trip-options" key={id}>
        <h5 className="wte-process-tab-title">
            <span dangerouslySetInnerHTML={{ __html: title }} />
            {required && <span className="wte-required" style={{ color: 'red' }}> *</span>}
        </h5>
        <div className={multiple && options.length > 1 ? "wte-es-with-multiple-options wte-owl-carousel" : ""}>
            <Component item={service} {...props} />
        </div>
    </div> : <Component item={service} {...props} key={id} />
}

export default ({ state, setState }) => {
    const { tripID, summary: { extraServices, selectedTripDate } } = state

    const api = `${wpApiSettings.root}wptravelengine/v2/trips/${tripID}/services${selectedTripDate ? `?date=${selectedTripDate}` : ''}`
    const { data: services = [], isLoading } = useQuery(['extra-services', selectedTripDate], () => fetch(api).then(r => r.json()))

    const computeProcessToNext = (list) => {
        const requiredServiceIds = services.filter(s => s.required).map(s => s.id)
        return requiredServiceIds.every(sid => list.some(e => e.service_id === sid && e.qty > 0))
    }

    useEffect(() => {
        setState({
            processToNext: computeProcessToNext(extraServices),
            isLoading
        })
    }, [isLoading, services, extraServices])

    const handleChange = service => ({ key: _key, price, label }, count) => {
        const { required, options, multiple, id: service_id } = service
        const total = parseFloat(price) * count
        let tempServices = extraServices

        label || (tempServices = tempServices.filter(({ id }) => !service.options.some(({ key }) => key === id)))

        tempServices.forEach(({ id }, index) => {
            if (options.find(({ key }) => key === id) && required) {
                tempServices[index].required = id === _key
                if (multiple) {
                    tempServices[index].required = (id === _key) ? options
                        .filter(({ key }) => key !== id)
                        .every(({ key }) => (tempServices
                            .find(({ id }) => id === key)?.qty ?? 0) === 0)
                        : tempServices[index].qty > 0
                }
            }
        })

        const serviceIndex = tempServices.findIndex(({ id }) => id === _key)
        if (serviceIndex !== -1) {
            tempServices[serviceIndex] = { ...tempServices[serviceIndex], qty: count, total }
        } else {
            const { price: unitPrice, serviceUnit, label } = options.find(({ key }) => key === _key)
            tempServices = [...tempServices, { id: _key, service_id, label, unitPrice, qty: count, total, perTextLabel: ` / ${serviceUnit.label}`, required }]
        }

        setState({
            processToNext: computeProcessToNext(tempServices),
            summary: { ...state.summary, extraServices: tempServices }
        })

    }

    const allSoldOut = Array.isArray(services) && services.length > 0 &&
        services.every(service => service.options?.every(opt => Number.isInteger(opt?.avaQty) && opt.avaQty === 0))

    if (allSoldOut && !applyFilters('wptravelengine.extraServices.showAllSoldOut', false, { services })) return null;

    const visibleServices = (Array.isArray(services) ? services : [])
        .filter(service => (service.options?.length ?? 0) > 0)
        .filter(service => applyFilters('wptravelengine.extraServices.renderService', true, { service }))

    if (!visibleServices.length) {
        return (
            <div className="wte-process-tab-content-wrapper">
                <Fallback
                    title={__('No Extra Services Available', 'wte-extra-services')}
                    content={__('There are no extra services available for this trip.', 'wte-extra-services')}
                />
            </div>
        )
    }

    return (
        <div className="wte-process-tab-content-wrapper">
            <h5 className="wte-process-tab-title">{__('Select Extra Services: ', 'wp-travel-engine')}</h5>
            {applyFilters('wptravelengine.tripBookingModal.extraServicesContent', <PricingTable>
                {() => visibleServices.map((service, index) => {
                    service = applyFilters(
                        'wptravelengine.extraServices.service',
                        service,
                        { index }
                    )

                    return withMultipleOptions(ServiceItem)({
                        service,
                        onChange: handleChange(service),
                        state,
                    })
                })}
            </PricingTable>, { services: visibleServices, onChange: handleChange, state, setState })}
        </div>
    )
}

const Fallback = styled(FallbackBox)`
    background-color: rgba(var(--wpte-primary-color-rgb, var(--primary-color-rgb)), 0.1) !important;
    border: 1px solid rgba(var(--wpte-primary-color-rgb, var(--primary-color-rgb)), 0.5) !important;
`
